import SidePanelHeader from "../components/SidePanelHeader"
import { useNavigate } from "react-router"
import * as Navigation from "../../../../../modules/Navigation";
import { activeTabSignal, globalCanSubmitSignal, globalListReloadSignal, globalLoadingSignal, globalSubmittingSignal, movingSidePanelSignal } from "../../Layout";
import { httpPost } from "../../../../../modules/Backend";
import { HttpStatusCode } from "axios";
import { v4 as uuidv4 } from 'uuid';
import LoadingButton from "../../../LoadingButton";
import { useSignals } from "@preact/signals-react/runtime";
import { modalSignal } from "../../../Modal/ModalContainer";
import ConfirmModal, { ConfirmType } from "../../../Modal/ConfirmModal";
import { EntityNames } from "../../../../../enums/EntityNames";
import Form from "../../../entityComponents/Form/Form";
import { useState, useEffect } from "react";
import SidePanelInfo from "../components/SidePanelInfo";
import SidePanelOuterCubeMaterial from "../components/SidePanelOuterCubeMaterial";
import SidePanelPercentageFull from "../components/SidePanelPercentageFull";
import { getStatus } from "../../../../../enums/Domain/StatusLookup";
import React from "react";

export default function SidePanelWorkstations({ data, onViewPage }: any) {
    useSignals();
    const navigate = useNavigate()
    const [hasOuterCube, setHasOuterCube] = useState<boolean | undefined>(!!data.currentOuterCubeId);

    const descriptor = activeTabSignal.value.index === 0
        ? `Add new outer cube to ${data.name}`
        : "Search for a part full to work with"

    function viewEntity() {
        Navigation.view.navigate(navigate, EntityNames.workstations, data.id, data)
    }

    async function handleAssign() {
        globalLoadingSignal.value = { isBusy: true };
        const payload = { id: data.id }
        const result = await httpPost(`api/${EntityNames.workstations}/assign`, payload);
        globalLoadingSignal.value = { isBusy: false };
        if (result.status === HttpStatusCode.Ok) {
            globalListReloadSignal.value = { hashValue: uuidv4() }
            const newData = { ...data };
            newData.isMine = result.data.isOccupied;

            if (newData.isMine) {
                // Indicate that this component should be re-rendered based on the result of this operation
                // This acts as a very smooth toggle of state from assigned -> unassigned and vice versa
                movingSidePanelSignal.value = {
                    ...movingSidePanelSignal.value,
                    component: <SidePanelWorkstations data={newData} onViewPage={onViewPage} />,
                }
            } else {
                window.location.reload();
            }
        }
    }

    function cancelAssign() {
        globalLoadingSignal.value = { isBusy: false }
    }

    function checkUnassign() {
        modalSignal.value = <ConfirmModal
            heading="Unassign from workstation?"
            information="You can reassign at any point"
            ContinueFunc={handleAssign}
            cancelFunc={cancelAssign}
            type={ConfirmType.Destructive} />
    }

    useEffect(() => {
        // Update hasOuterCube when data.currentOuterCubeId changes (e.g., when outer cube is deleted)
        setHasOuterCube(!!data.currentOuterCubeId);
    }, [data.currentOuterCubeId]);

    function getOuterCubeTypeForDisplay() {
        if (data?.currentOuterCubeType === null || data?.currentOuterCubeType === undefined) {
            return '';
        }
        const status = getStatus(data.currentOuterCubeType);
        return status?.displayValue;
    }

    async function confirmPalletDetails(submit: Function, formData: any) {
        modalSignal.value = <ConfirmModal
            heading={`Please check pallet details`}
            information="You cannot change the pallet type once it has been created. Pallet must be deleted and re-added."
            ContinueFunc={submit}
            cancelFunc={() => {
                globalSubmittingSignal.value = { isBusy: false };
                globalCanSubmitSignal.value = { allowed: true };
                modalSignal.value = undefined;
            }} />
    }

    return (<>
        {data &&
            <>
                {!onViewPage && data.isMine &&
                    <SidePanelHeader descriptor="" value={data.name} statuses={[]} onClick={(() => { viewEntity() })} />
                }
                {onViewPage && data &&
                    <SidePanelHeader descriptor={descriptor} value="" statuses={[]} onClick={undefined} />
                }
                {!onViewPage && !data.isMine &&
                    <h2>{data.name}</h2>
                }
                <div className={`display--flex flex__wrap--wrap justifyContent--space-between margin__top--s`}>
                    {!onViewPage && !data.assignedOperatorId && !data.isMine &&
                        <LoadingButton isDisabled={globalLoadingSignal.value.isBusy}
                            className="button button--small button--primary"
                            isLoading={globalLoadingSignal.value.isBusy}
                            buttonText="Assign me"
                            loadingText="Assigning"
                            onPress={handleAssign}>
                            <></>
                        </LoadingButton>
                    }
                    {!onViewPage && data.isMine &&
                        <LoadingButton isDisabled={globalLoadingSignal.value.isBusy}
                            className="button button--small button--primary--destructive"
                            isLoading={globalLoadingSignal.value.isBusy}
                            buttonText="Unassign me"
                            loadingText="Uassigning"
                            onPress={checkUnassign}>
                            <></>
                        </LoadingButton>
                    }
                </div>
                {!onViewPage && data && data.currentOuterCubeId &&
                    <>
                        <SidePanelInfo
                            descriptor="Current Outer Cube"
                            values={[
                                { label: "Reference number", value: data.currentOuterCubeReference },
                                { label: "Fullness", value: data.currentOuterCubeFullness },
                                { label: "Belongs to", value: data.currentOuterCubeBelongsTo },
                                { label: "Type", value: getOuterCubeTypeForDisplay() },
                                { label: "Current capacity", value: <SidePanelPercentageFull percentageFull={data.currentOuterCubePercentageFull} /> }]}
                        />
                        <SidePanelOuterCubeMaterial data={{ id: data.currentOuterCubeId }} />
                    </>
                }
                {!onViewPage && data && !data.currentOuterCubeId &&
                    <div className="side-panel__info-box margin__top--s">
                        <p className="small margin__bottom--xs">No outer cube present</p>
                    </div>
                }
                {onViewPage && data && activeTabSignal.value.index === 0 &&
                    <Form isCreate={true} entityName={EntityNames.outerCubes}
                        parentId={data.id}
                        isInline={true}
                        submitOnly={true}
                        clearAfterSubmit={true}
                        submitNotAllowed={hasOuterCube}
                        submitFunction={confirmPalletDetails}
                        onSubmitCallback={() => setHasOuterCube(true) } />
                }
                {onViewPage && data && hasOuterCube && activeTabSignal.value.index === 0 &&
                    <div className="box box--warning width--full">
                        <p>This workstation is occupied, clear it to add a new pallet</p>
                    </div>
                }
            </>
        }
    </>)
}
